import { useRef } from "react";
import "./PanelConfiguracion.css";

/* ============================================================================
   SISTEMA-DE-DISENO · patrón Configuración.

   Índice de secciones a la izquierda, contenido a la derecha. Es el cuarto
   patrón de pantalla del sistema y vive aquí una sola vez: ninguna pantalla
   vuelve a maquetar su propia botonera de pestañas.

   Por qué a la izquierda y no arriba: con trece secciones, una tira horizontal
   obliga a un scroll lateral en el que la sección activa desaparece de la
   vista. En vertical caben todas, se leen de un vistazo y el nombre no se
   corta.

   Las secciones se agrupan y el grupo se ETIQUETA, porque no todas tienen el
   mismo alcance: cambiar un tarifario afecta a toda la clínica, cambiar el
   tema afecta sólo a quien lo cambia. Eso hay que decirlo antes de que alguien
   toque el control, no después.

   Accesibilidad: es un conjunto de pestañas real (`tablist`/`tab`/`tabpanel`),
   con flechas para moverse entre secciones y `aria-selected` en la activa.
   ========================================================================== */

function PanelConfiguracion({ grupos, activa, onCambiar, children, etiqueta = "Secciones de configuración" }) {
    const listaRef = useRef(null);

    const todas = (grupos || []).flatMap((g) => g.secciones || []);

    const alTeclado = (e) => {
        const teclas = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 };
        const paso = teclas[e.key];
        if (!paso) return;
        e.preventDefault();
        const i = todas.findIndex((s) => s.clave === activa);
        const siguiente = todas[(i + paso + todas.length) % todas.length];
        if (siguiente) {
            onCambiar(siguiente.clave);
            // El foco viaja con la selección: si no, la flecha mueve la vista
            // pero deja el teclado donde estaba.
            const boton = listaRef.current?.querySelector(`[data-seccion="${siguiente.clave}"]`);
            boton && boton.focus();
        }
    };

    return (
        <div className="cfg" data-testid="cfg">
            <div className="cfg__indice" ref={listaRef} role="tablist" aria-orientation="vertical" aria-label={etiqueta} onKeyDown={alTeclado}>
                {(grupos || []).map((grupo) => (
                    <div className="cfg__grupo" key={grupo.titulo}>
                        <div className="cfg__grupo-titulo">{grupo.titulo}</div>
                        {grupo.alcance && <div className="cfg__grupo-alcance">{grupo.alcance}</div>}
                        {(grupo.secciones || []).map((s) => (
                            <button
                                key={s.clave}
                                type="button"
                                role="tab"
                                id={`cfg-tab-${s.clave}`}
                                aria-selected={activa === s.clave}
                                aria-controls={`cfg-panel-${s.clave}`}
                                tabIndex={activa === s.clave ? 0 : -1}
                                data-seccion={s.clave}
                                className={`cfg__item${activa === s.clave ? " is-activa" : ""}`}
                                onClick={() => onCambiar(s.clave)}
                            >
                                {s.etiqueta}
                            </button>
                        ))}
                    </div>
                ))}
            </div>

            <div
                className="cfg__contenido"
                role="tabpanel"
                id={`cfg-panel-${activa}`}
                aria-labelledby={`cfg-tab-${activa}`}
                tabIndex={0}
                data-testid="cfg-contenido"
            >
                {children}
            </div>
        </div>
    );
}

/**
 * Una sección de ajustes. `alcance` no es decorativo: dice a quién afecta lo
 * que se cambie aquí, y es lo primero que se lee.
 */
export function SeccionAjustes({ titulo, descripcion, alcance, children }) {
    return (
        <section className="cfg-sec" data-testid="cfg-seccion">
            <header className="cfg-sec__cabecera">
                <h2 className="cfg-sec__titulo">{titulo}</h2>
                {alcance && <span className="cfg-sec__alcance">{alcance}</span>}
            </header>
            {descripcion && <p className="cfg-sec__descripcion">{descripcion}</p>}
            <div className="cfg-sec__filas">{children}</div>
        </section>
    );
}

/**
 * UNA FILA = UNA DECISIÓN: nombre, explicación debajo, control a la derecha.
 * Nada de formularios de treinta campos sin agrupar.
 */
export function FilaAjuste({ nombre, explicacion, children }) {
    return (
        <div className="cfg-fila" data-testid="cfg-fila">
            <div className="cfg-fila__texto">
                <span className="cfg-fila__nombre">{nombre}</span>
                {explicacion && <span className="cfg-fila__explicacion">{explicacion}</span>}
            </div>
            <div className="cfg-fila__control">{children}</div>
        </div>
    );
}

/**
 * Grupo de opciones excluyentes presentado como segmentos. Se usa cuando las
 * opciones son pocas y conviene verlas todas a la vez -- elegir un tema con un
 * desplegable obliga a abrirlo para saber qué hay.
 */
export function Segmentos({ opciones, valor, onCambiar, etiqueta }) {
    return (
        <div className="cfg-seg" role="radiogroup" aria-label={etiqueta}>
            {(opciones || []).map((o) => (
                <button
                    key={o.value}
                    type="button"
                    role="radio"
                    aria-checked={valor === o.value}
                    className={`cfg-seg__opcion${valor === o.value ? " is-activa" : ""}`}
                    onClick={() => onCambiar(o.value)}
                    title={o.hint}
                    data-testid={`cfg-seg-${o.value}`}
                >
                    {o.label}
                </button>
            ))}
        </div>
    );
}

export default PanelConfiguracion;
